<template>
  <Sheet>
    <img
      :src="MapGray"
      class="pointer-events-none absolute top-0 left-0 h-[180px] w-full object-cover"
    />
    <template #leading>
      <Sheet.Button :icon="CloseIcon" @click="close()" />
    </template>
    <template #trailing>
      <Sheet.Button
        variant="primary"
        :icon="CheckmarkIcon"
        @click="handleConfirm"
      />
    </template>

    <Sheet.Body class="px-0!">
      <div class="relative">
        <div
          class="pointer-events-none absolute top-0 left-0 h-[180px] w-full bg-gradient-to-b from-transparent to-white"
        />

        <div class="relative px-6 pt-2.5">
          <div class="mb-[34px] flex flex-col items-center">
            <h2
              class="mb-1.5 text-center font-primary text-[26px] font-semibold leading-[34px] text-gray-800"
            >
              Register under national laws
            </h2>
            <p
              class="text-center font-secondary text-[14px] leading-[22px] text-gray-800"
            >
              Choose default countries to have your work recognized and
              registered under their copyright laws and applicable legal
              remedies.
            </p>
            <a
              href="/member-states"
              target="_blank"
              class="mt-3 font-primary text-[14px] font-medium text-green-700"
            >
              Learn more and registration costs
            </a>
          </div>

          <div
            class="mb-[14px] font-secondary text-[14px] leading-[22px] text-gray-800/50"
          >
            Selected jurisdictions to be registered
          </div>

          <div class="grid grid-cols-2 items-center gap-2">
            <div
              v-for="origin in selectedOrigins"
              :key="origin.id"
              class="flex items-center gap-x-2 rounded-[22px] border-2 border-green-500 bg-green-200/20 py-1.5 pr-3 pl-1.5"
              :class="isLocked(origin.id) ? '' : 'cursor-pointer'"
              @click="toggle(origin.id)"
            >
              <div
                v-if="origin.id === 'Dacr'"
                class="flex size-[26px] items-center justify-center rounded-full border border-gray-600/15 bg-white"
              >
                <DacrMarkIcon class="size-[18px] text-gray-800" />
              </div>
              <Avatar v-else :src="origin.flag.url" :size="26" />
              <div
                class="flex-1 truncate font-secondary text-[13px] font-medium leading-[15px] text-gray-800"
              >
                {{ origin.name }}
              </div>
              <Checkbox :model-value="true" :disabled="isLocked(origin.id)" />
            </div>
          </div>

          <template v-if="availableOrigins.length">
            <div
              class="mt-6 mb-[14px] font-secondary text-[14px] leading-[22px] text-gray-800/50"
            >
              Other jurisdictions
            </div>

            <div class="grid grid-cols-2 items-center gap-2">
              <div
                v-for="origin in availableOrigins"
                :key="origin.id"
                class="flex cursor-pointer items-center gap-x-2 rounded-[22px] border border-black/8 bg-white py-1.5 pr-3 pl-1.5 transition-colors hover:border-black/15"
                @click="toggle(origin.id)"
              >
                <Avatar :src="origin.flag.url" :size="26" />
                <div
                  class="flex-1 truncate font-secondary text-[13px] font-medium leading-[15px] text-gray-800"
                >
                  {{ origin.name }}
                </div>
                <Checkbox :model-value="false" />
              </div>
            </div>
          </template>
        </div>
      </div>
    </Sheet.Body>
  </Sheet>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Sheet, Avatar, Checkbox, useSheet } from '@dacr/library'
import { DacrMarkIcon, CloseIcon, CheckmarkIcon } from '@dacr/icons'
import MapGray from '@/assets/images/MapGray.png'
import { useUploadStore } from '@/features/upload/store'
import { useOriginStore } from '@/shared/stores/origin'
import { useUserStore } from '@/shared/stores/user'

const { close } = useSheet()
const uploadStore = useUploadStore()
const originStore = useOriginStore()
const userStore = useUserStore()

const lockedOriginId = computed(() => userStore.user?.origin ?? null)

const selectedIds = ref(new Set(uploadStore.draft?.originIds ?? ['Dacr']))

const isLocked = (id: string) => id === 'Dacr' || id === lockedOriginId.value

const selectedOrigins = computed(() =>
  originStore.origins.filter((origin) => selectedIds.value.has(origin.id))
)

const availableOrigins = computed(() =>
  originStore.origins.filter(
    (origin) => !isLocked(origin.id) && !selectedIds.value.has(origin.id)
  )
)

const toggle = (id: string) => {
  if (isLocked(id)) return
  const next = new Set(selectedIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  selectedIds.value = next
}

const handleConfirm = () => {
  uploadStore.updateDraft({ originIds: [...selectedIds.value] })
  close()
}
</script>
